'use client';

import { useMemo } from 'react';
import dynamic from 'next/dynamic';
import type { ApexOptions } from 'apexcharts';
import { getTerraGraphCategoryColors } from '@terra/ui-styled-system';
import { useThemeMode } from '@terra/ui-design-system';
import type { DonutTooltipParams } from './format';

const ReactApexChart = dynamic(() => import('react-apexcharts'), {
  ssr: false,
});

export interface PieChartProps {
  /**
   * Array of numeric values for each pie slice
   */
  series: number[];
  /**
   * Labels for each pie slice
   */
  labels: string[];
  /**
   * Optional title for the chart
   */
  title?: string;
  /**
   * Height of the chart
   */
  height?: number | string;
  /**
   * Width of the chart
   */
  width?: number | string;
  /**
   * Array of colors for pie slices
   */
  colors?: string[];
  /**
   * Whether to show data labels on pie slices
   */
  showDataLabels?: boolean;
  /**
   * Custom tooltip renderer
   */
  tooltipCustom?: (options: DonutTooltipParams) => string;
  /**
   * Custom formatter for tooltip values
   */
  tooltipFormatter?: (value: number) => string;
  /**
   * Whether to show the legend
   */
  showLegend?: boolean;
  /**
   * Position of the legend
   */
  legendPosition?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Callback function when a data point is clicked
   */
  onDataPointClick?: (
    dataPointIndex: number,
    label: string,
    value: number,
  ) => void;
}

export function PieChart({
  series,
  labels,
  title = '',
  height = 350,
  width = '100%',
  colors = getTerraGraphCategoryColors(),
  showDataLabels = true,
  tooltipCustom,
  tooltipFormatter,
  showLegend = true,
  legendPosition = 'bottom',
  onDataPointClick,
}: PieChartProps) {
  const { mode: themeMode } = useThemeMode();

  const options = useMemo<ApexOptions>(() => {
    if (!series || !labels || series.length !== labels.length) {
      console.error('PieChart: Invalid or mismatched series and labels');

      return {
        chart: {
          type: 'pie',
          toolbar: {
            show: false,
          },
        },
      };
    }

    return {
      chart: {
        type: 'pie',
        toolbar: {
          show: false,
        },
        foreColor: 'currentColor',
        animations: {
          enabled: true,
          speed: 300,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
        events: {
          dataPointSelection: onDataPointClick
            ? (event, chartContext, opts) => {
                const { dataPointIndex } = opts;
                const label = labels[dataPointIndex];
                const value = series[dataPointIndex];

                onDataPointClick(dataPointIndex, label, value);
              }
            : undefined,
        },
      },
      labels,
      colors,
      dataLabels: {
        enabled: showDataLabels,
        formatter: (val: number) => {
          return [val.toFixed(1) + '%'];
        },
        style: {
          fontSize: '14px',
          fontFamily: 'inherit',
          fontWeight: 'normal',
        },
        dropShadow: {
          enabled: true,
        },
      },
      title: {
        text: title,
        align: 'left',
        style: {
          fontSize: '16px',
          fontWeight: 'normal',
        },
      },
      tooltip: {
        theme: themeMode === 'dark' ? 'dark' : 'light',
        enabled: true,
        ...(tooltipCustom && { custom: tooltipCustom }),
        y: {
          ...(tooltipFormatter && { formatter: tooltipFormatter }),
        },
      },
      legend: {
        show: showLegend,
        position: legendPosition,
        fontSize: '14px',
        fontFamily: 'inherit',
        labels: {
          useSeriesColors: false,
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 300,
            },
            legend: {
              position: 'bottom',
            },
          },
        },
      ],
      stroke: {
        width: 0,
      },
      states: {
        hover: {
          filter: {
            type: 'darken',
            value: 0.15,
          },
        },
        active: {
          allowMultipleDataPointsSelection: false,
          filter: {
            type: 'darken',
            value: 0.35,
          },
        },
      },
    };
  }, [
    series,
    labels,
    colors,
    showDataLabels,
    title,
    tooltipCustom,
    tooltipFormatter,
    showLegend,
    legendPosition,
    themeMode,
    onDataPointClick,
  ]);

  if (!options) {
    return null;
  }

  return (
    <ReactApexChart
      options={options}
      series={series}
      type="pie"
      height={height}
      width={width}
    />
  );
}

export default PieChart;
